import { Placement } from '@floating-ui/dom';
import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { nanoid } from 'nanoid';
import { ArrowNavigationController } from '../../controllers/arrowNavigation';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksSearchEvent } from '../../events';
import { BlocksChangeEvent } from '../../events/blocks-change';
import { BlocksCloseEvent } from '../../events/blocks-close';
import { BlocksMobileOverlayCloseEvent } from '../../events/blocks-close-mobile-overlay';
import { BlocksMobileOverlayOpenEvent } from '../../events/blocks-mobile-overlay-open';
import { BlocksOpenEvent } from '../../events/blocks-open';
import type { AutoPositionInterface, Position } from '../../util/common-modal';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { isPopoverSupported } from '../../util/popover';
import { scrollIntoView } from '../../util/scroll';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import { BlocksCheckboxField } from '../checkbox-field/checkbox-field';
import '../dropdown-panel/dropdown-panel';
import { BlocksDropdownPanel } from '../dropdown-panel/dropdown-panel';
import '../field-note/field-note';
import '../icon/icons/arrow-back';
import '../icon/icons/close';
import '../icon/icons/search';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import type { AutocompleteAttribute } from '../input-field/input-field.model';
import { BlocksListItem } from '../list-item/list-item';
import { BlocksList } from '../list/list';
import '../skeleton/skeleton';
import type { SearchFormPosition, SearchFormSize, SearchFormStatus } from './search-form.model';
import styles from './search-form.styles';

/**
 * @slot before - The component before content which will be placed at submit-button before slot
 * @slot default - The component content where the panel displays
 * @slot custom - The component content which displays along with search-form input
 * @slot after - The component after content which will be placed at submit-button after slot
 * @slot error - errorNote slot
 * @slot field-note - fieldNote slot
 * @slot warning - warningNote slot
 * @fires bl-change - emitted when value is changed => Emits null when clear button is clicked . Emits empty string when users clears using keydown.
 * @fires bl-search-open - emitted when panel is opened
 * @fires bl-search-close - emitted when panel is closed
 * @fires bl-mobile-overlay-open - emitted when panel is opened in mobile view.
 * @fires bl-mobile-overlay-close - emitted when panel is closed in mobile view.
 * @fires bl-search - emitted when search icon button is clicked
 * @fires bl-scroll-end - emitted according to scrollOffset  values, by default it is zero so emitted at the end of dropdown panel.
 * @csspart input - The component's input field element
 * @csspart base - The component's base wrapper.
 * @csspart label-loader - The component's label-loader wrapper.
 * @csspart body-loader - The component's body-loader.
 * @csspart label-skeleton - The component's label-skeleton.
 * @csspart body-skeleton - The component's body-skeleton element.
 * @csspart icon-search - The component's icon-search element.
 * @csspart field-note-loader - The component's field-note-loader.
 * @csspart field-note-skeleton - The component's field-note-skeleton.
 * @csspart body - The component's body wrapper.
 * @csspart back-button - The component's back-button element.
 * @csspart icon-arrow-back - The component's arrow-back icon.
 * @csspart typeahead-wrapper - The component's typeahead wrapper.
 * @csspart typeahead - The component's typeahead container.
 * @csspart part-label - The component's label wrapper.
 * @csspart icon-container - The component's icon container.
 * @csspart icon-after - The component's icon-after wrapper.
 * @csspart clear-button - The component's clear-button.
 * @csspart icon-close - The component's close icon.
 * @csspart status-icon - The component's status-icon.
 * @csspart separator - The component's separator wrapper.
 * @csspart dropdown-panel - The component's dropdown-panel element.
 * @csspart part-default - The component's default slot.
 * @csspart part-custom - The component's custom slot.
 * @csspart submit - The component's submit button.
 * @csspart after-wrapper - The component's submit button after wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart before-wrapper - The component's submit button before wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-error - The component's error slot.
 * @csspart part-warning - The component's warning slot.
 * @csspart accessibility-list - The component's list for accessibility.
 */

export class BlocksSearchForm extends BlocksElement implements AutoPositionInterface {
  static formAssociated = true;static styles: CSSResultGroup = styles;
  protected autoPositionController = new AutoPositionController(this);

  /**
   * The unique id of the field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * Visually hide button text (but text is still accessible to assistive technology)
   * 1) Use this for icon-only button variant for accessibility
   */
  @property({ type: Boolean })
  hideText = false;

  /**
   * Label of the form field
   */
  @property({ type: String })
  label = 'Search';

  /**
   * Name attribute
   */
  @property({ type: String, reflect: true })
  name: string | undefined;

  /**
   * Description for the field
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   *  Error message for the field
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   *  Warning message for the field
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Required attribute
   * @ignore
   */
  @property({ type: Boolean })
  required: boolean | undefined;

  /**
   * Search button text
   */
  @property({ type: String })
  buttonText: string | undefined;

  /**
   * Clear button text
   */
  @property({ type: String })
  clearButtonText = 'Clear text';

  /**
   * back button  text in mobile variant
   */
  @property({ type: String })
  backButtonText = 'Back';

  /**
   * Disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * Active state
   */
  @property({ type: Boolean })
  isActive = false;

  /**
   * Placeholder text
   */
  @property({ type: String })
  placeholder = 'Find asset';

  /**
   * Input value
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the field note and errorNote for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Provides an accessible name for the cat list
   */
  @property({ type: String })
  arialListLabel: string | undefined;

  /**
   * Child list role
   * For cat-list, role can be "listbox"
   * For checkbox group, role can be "group"
   */
  @property({ type: String })
  arialListRole = 'listbox';

  /**
   * This is used for mobileView
   */
  @property({ type: Boolean })
  isOverlay: boolean | undefined;

  /**
   * Specifies the bottom offset for the dropdown panel to determine when lazy loading should be triggered.
   * The value is a string representing the bottom offset in the format 'px' or '%'.
   * Default value is '0px'.
   */

  @property({ type: String })
  scrollOffset = '0px';

  /**
   * Query the checkbox list
   */
  private get _checkboxList() {
    return Array.from(this.querySelectorAll<BlocksCheckboxField>('cat-checkbox-field'));
  }

  /**
   * Query the checkbox list to filter out disabled items that do not have hashOver set to true.
   */
  private get _checkboxHasHoverList() {
    return (this._checkboxList || []).filter((item) => !item.disabled || item.hasHover);
  }

  /**
   * Size
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** increases the search height with label inside</li>
   * <li>**md** reduce the search height with label outside </li>
   * <li>**sm** reduce  the search height without label</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: SearchFormSize = 'lg';

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Status of datepicker input field
   */
  @property({ type: String })
  status: SearchFormStatus;

  /**
   * Maxlength of characters for the search form
   */
  @property({ type: Number })
  maxlength: number | undefined;

  /**
   * Use this Flag to not display dropdown panel
   * Used in dropdown with search variant.
   *
   */
  @property({ type: Boolean })
  isEmpty = false;

  /**
   * Active element in the dropdown panel
   * 1) This is used to read out the active value to the user
   */
  @property({ type: String })
  ariaActiveDescendantId: string | undefined;

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the panel will be opened inside the top layer.
   * If none of the position fits and if it goes out of viewport, scroll will be enabled if `isInline` is false.
   * If `isInline` is true, the popover attribute will not be used, and the panel
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * **Search dropdown position**
   * - **bottom** search dropdown panel appears on the bottom
   * - **top** search dropdown panel appears on the top
   * If `isInline` is false, and if specified position doesn't fit inside the viewport,
   * it will try to accommodate in other positions which fits best.
   */
  @property({ type: String })
  position: SearchFormPosition = 'auto';

  /**
   * **Breakpoint below which search form switches to mobile view.Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * AutoFocus attribute
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * **Boolean value which opens Search Form on mouse click and Enter keydown**
   */
  @property({ type: Boolean })
  openOnClick = false;

  /**
   * **Boolean value which hides the search icon inside typeahead-wrapper**
   */
  @property({ type: Boolean })
  hideSearchIcon = false;

  /**
   * Autocomplete property, by default it would be on
   */
  @property({ type: String })
  autocomplete: AutocompleteAttribute = 'on';

  /**
   * Name or Title for the warning icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  warningIconTitle = 'warning icon';

  /**
   * Name or Title for the error icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  errorIconTitle = 'error icon';

  /**
   * Current selected list-item in the dropdown panel
   */
  @state() private currentSelectedItem: BlocksListItem | BlocksCheckboxField | undefined | null;

  /**
   * isFocusedIn - To specify whether the focus is inside the dropdown panel
   */
  @state() private isFocusedIn: boolean | undefined;

  /**
   * Mobile Variant
   */
  @state() private isMobile = false;

  @query('input') private input!: HTMLInputElement;

  /**
   * Query the Search Form base Element
   **/
  @query('.cat-c-search-form') private _searchFormElement!: HTMLElement;

  /**
   * Query the Mobile Search Form base Element
   **/
  @query('.cat-c-search-form--mobile') private _searchFormMobileBody!: HTMLElement;

  /**
   * Query the Mobile Input Form base Element
   **/
  @query('.cat-c-search-form--mobile .cat-c-search-form__input') private _searchMobileInput!: HTMLInputElement;

  protected slots = new SlotController(this, 'before', null, 'custom', 'after', 'error', 'field-note', 'warning');

  /**
   * Identify the current focused index
   */
  private currentFocusedIndex = -1;

  private internals_ = this.attachInternals?.();

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  /**
   * Query the Search Form Input
   **/
  private get _catSearchFormInput(): HTMLElement {
    return this.shadowRoot?.querySelector<HTMLElement>('.cat-c-search-form__typeahead-wrapper')!;
  }

  /**
   * Query the Search Form Input Html Element
   */
  private get _catInputElement(): HTMLInputElement {
    return this.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-search-form__input')!;
  }

  /**
   * Query the Back button
   */
  private get _catBackButton() {
    return this.shadowRoot?.querySelector<HTMLElement>('.cat-c-search-form__back-button')!;
  }

  /**
   * Query the dropdownpanel body
   **/
  private get _catDropdownPanel(): HTMLElement {
    return this.shadowRoot?.querySelector<HTMLElement>('.cat-c-search-form__dropdown-panel')!;
  }

  /**
   * Query the Body
   */

  private get queryBody() {
    return document.querySelector('body')!;
  }

  /**
   * Used for multiple keys pressed at the same time
   */
  private keysPressed: Record<string, boolean> = {};

  private appBodyOverflow!: string;

  private ariaControlsId: string | undefined | null;

  /**
   * Positions used to display dropdownPanel body
   */
  private allowedPlacements = ['bottom-start', 'top-end', 'bottom-end', 'top-start'] as Placement[];

  private isPopoverSupported = isPopoverSupported();

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }

  private get hasErrorSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  private get hasWarningSlot(): boolean {
    return this.slots.hasSlotted('warning');
  }

  private get hasBeforeSlot(): boolean {
    return this.slots.hasSlotted('before');
  }

  private get hasAfterSlot(): boolean {
    return this.slots.hasSlotted('after');
  }

  protected arrowNavigationController = new ArrowNavigationController();

  private get isPopoverEnabled(): boolean {
    return this.isPopoverSupported && !this.isInline;
  }

  private get _catDropdownPanelBody(): HTMLElement {
    return this.shadowRoot?.querySelector('cat-dropdown-panel')?.shadowRoot?.querySelector('.cat-c-dropdown-panel__body')!;
  }

  public getFloatingElement(): HTMLElement {
    return this._catDropdownPanel;
  }
  public getSync(): string {
    return 'width';
  }
  public getPosition(): Position {
    return this.position;
  }
  public getIsInline(): boolean {
    return this.isInline;
  }
  public getReferenceElement(): HTMLElement {
    return this._catSearchFormInput;
  }
  public getOverflowElement(): HTMLElement | null {
    return this._catDropdownPanelBody;
  }
  public getAllowedPlacements(): Placement[] {
    return this.allowedPlacements;
  }

  /**
   * First updated lifecycle when component is first updated on page
   */
  firstUpdated() {
    this.setMobile();
    this.catListUpdated();
  }

  /**
   * Sets the dropdown panel if openOnClick is true in mobile view
   */
  private dropdownPanelOpenOnClick(): void {
    if (!this.isMobile) {
      return;
    }

    this.openOnClick ? this.setDropdownPanelDisplay('block') : this.setDropdownPanelDisplay('none');
  }

  /**
   * Sets the dropdown panel display
   */
  private setDropdownPanelDisplay(display: 'block' | 'none'): void {
    if (!this._catDropdownPanel) {
      return;
    }

    this._catDropdownPanel.style.display = display;
  }

  /**
   * Sets mobile variant to active if screen size is below mobileBreakPoint property.
   */
  private setMobile = () => {
    const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (width < this.mobileBreakPoint) {
      this.isMobile = true;
    } else {
      if (this.isActive) {
        this.isActive = false;
        this.emitMobilePanelCloseEvent();
      }
      this.updateComplete.then(() => {
        this.isMobile = false;
      });
    }
  };

  /**
   * Connected callback lifecycle
   */
  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
    window.addEventListener('resize', this.setMobile);
  }

  /**
   * Disconnected callback lifecycle
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    window.removeEventListener('resize', this.setMobile);
  }

  /**
   * Updated lifecycle hook
   */
  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isActive')) {
      if (this.isActive) {
        if (this.isMobile) {
          this.mobileClick();
        } else {
          this.openPanel();
        }
      } else {
        if (this.isMobile) {
          this.closeMobile();
        } else {
          this.closePanel();
        }
      }
      if (_changedProperties.has('value')) {
        this.internals_?.setFormValue(this.value ?? null);
      }
    }

    if (_changedProperties.has('value') && this.value === '') {
      this.removeAriaSelected();
    }

    if (_changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }

    if (_changedProperties.has('isEmpty')) {
      if (!this.isEmpty) {
        this.ariaControlsId = nanoid();
      } else {
        this.ariaControlsId = null;
      }
    }
  }

  override focus(): void {
    this.input?.focus();
  }

  /**
   * Change output binding
   * 1) If has input value then show the dropdown panel and clear icon
   * 2) Emit target value to outside of component to be used at application level
   * 3) To handle the announcement of the available results count on search
   */
  private onChanged = ({ target }: Event) => {
    this.value = (target as HTMLInputElement).value;

    if (this.value?.length) {
      if (this.isMobile) {
        this.setDropdownPanelDisplay('block');
      }

      if (!this.isActive) {
        this.isActive = true; /* 1 */
        if (this.isMobile) {
          this.emitMobilePanelOpenEvent();
        } else {
          this.emitPanelOpenEvent(); /* 1 */
        }
      }
    } else {
      this.dropdownPanelOpenOnClick();

      if (this.isActive) {
        this.updateScroll();
        if (!this.isMobile) {
          this.isActive = false;
          this.emitPanelCloseEvent();
        }
      }
    }
    this.emitOnChangeEvent(this.value);
  };

  /**
   * Emit open event when panel is opened
   */
  private emitPanelOpenEvent() {
    this.dispatch({ eventName: 'bl-search-open', detailObj: { isActive: true } }) as BlocksOpenEvent;
  }

  /**
   * Emit close event when panel is closed
   */
  private emitPanelCloseEvent() {
    this.dispatch({ eventName: 'bl-search-close', detailObj: { isActive: false } }) as BlocksCloseEvent;
  }

  /**
   * Close dropdown panel
   */
  private closePanel() {
    if (!this.isMobile) {
      this.setDropdownPanelDisplay('none');
      this.autoPositionController.closeOverlay();
    }
  }

  /**
   * Open dropdown panel
   */
  private openPanel() {
    if (!this.isMobile) {
      if (this._catSearchFormInput && this._catDropdownPanel) {
        this.autoPositionController.openOverlay();
      }
    }
  }

  /**
   * When the component first loads
   * 1) If the dropdown has a list as children, add the proper aria attributes to the `list`
   * and `list-item` slotted components to get proper accessibility
   */
  private catListUpdated() {
    if (this.children[0]?.outerHTML.includes('cat-list')) {
      const thisList = this.querySelector('cat-list')!;

      setTimeout(() => {
        thisList.listRole = 'listbox';
        if (this.arialListLabel) {
          thisList.arialabel = this.arialListLabel;
        }
        const listItems = Array.from(this.querySelectorAll('cat-list-item'));
        listItems.forEach((item: BlocksListItem) => {
          item.listItemRole = item.listItemRole !== 'listitem' ? item.listItemRole : 'option';
          item.setAttribute('id', nanoid());
        });
      }, 1);
    }
  }

  /**
   * Clear the input field on click
   * 1) Set active, clear after click
   * 2) Clear the input value after click
   * 3) Set the focus on the input after the clear
   * 4) @fires change event when we click on close icon
   */
  private clearSearchForm() {
    this.value = '';
    this._catInputElement.value = ''; /* 2 */

    this._catInputElement.focus(); /* 3 */
    this.emitOnChangeEvent(null); /* 4 */
    this.isFocusedIn = false;
    this.updateScroll();
    if (!this.isActive) {
      return;
    }

    if (!this.isMobile) {
      this.isActive = false;
      this.emitPanelCloseEvent();
    }

    this.dropdownPanelOpenOnClick();
  }

  /**
   * Emit onChange event
   */
  private emitOnChangeEvent(value: string | null): void {
    this.dispatch({ eventName: 'bl-change', detailObj: { value } }) as BlocksChangeEvent;
    this.assignSearchText(this.value as string);
  }

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the nav is already closed or if it is a mobile then we don't care about outside clicks and we can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be defined, but TypeScript isn't that trusting and sees shadowRoot as possibly undefined. 
    To work around that we'll check that we have a shadowRoot (and a rendered .host) element here to appease the TypeScript compiler. 
    This should never actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active panel
   * 5) If the panel is active and we've clicked outside of the panel then it should be closed.
   */
  private handleOnClickOutside = (e: MouseEvent) => {
    /* 2 */
    if (!this.isActive || this.isMobile) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine panel context during click handler');
    }

    /* 4 */
    const didClickInside = e.composedPath().includes(this.shadowRoot.host);

    /* 5 */
    if (this.isActive && !didClickInside) {
      setTimeout(() => {
        this.isActive = false;
        this.emitPanelCloseEvent();
      }, 1);
    }
  };

  /**
   * Set menu active state
   * 1) Toggle the active state between true and false
   * 2) If the active state is turned on, set focus to the first element in the dropdown panel
   * 3) If the active state is toggled to false, close the panel and return focus to the dropdown trigger
   * 4) If the event target has a value (list-item has value prop), then replace the input value with the list-item value
   */
  private toggleActive(e: Event) {
    this.isActive = !this.isActive;
    const inputElement = e.target as BlocksListItem;

    if (inputElement.value) {
      this._catInputElement.value = inputElement.value as string;

      this.value = inputElement.value as string;
      this.assignSearchText(this.value);
    }

    setTimeout(() => {
      this._catInputElement.focus();
    }, 100);
    if (this.isMobile) {
      this.emitMobilePanelCloseEvent();
      return;
    }
    if (!this.isActive) {
      this.updateScroll();
      this.removeAriaSelected();
      this.emitPanelCloseEvent();
      this.currentFocusedIndex = -1;
    }
  }

  /**
   * Select first item in dropdown
   * 1) Set the current selected item to the first item of the dropdown list
   * 2) Add `aria-selected` to first item to show selected state in listbox dropdown
   * 3) Change the `aria-activedescendant` value in input to the id of the selected item
   */
  private setFirstListItemAsCurrentItem() {
    const catListItems = Array.from(this.querySelectorAll('cat-list-item'));
    for (const element of catListItems) {
      if (!element?.isStatic) {
        this.currentSelectedItem = element; /* 1 */
        break;
      }
    }

    this.currentSelectedItem?.setAttribute('aria-selected', 'true'); /* 2 */
    this.ariaActiveDescendantId = this.currentSelectedItem?.getAttribute('id')!; /* 3 */
  }

  /**
   * Select last item in dropdown
   * 1) Set the current selected item to the last item of the dropdown list
   * 2) Add `aria-selected` to last item to show selected state in listbox dropdown
   * 3) Change the `aria-activedescendant` value in input to the id of the selected item
   */
  private setLastItemAsCurrentItem() {
    const catListItems = Array.from(this.querySelectorAll('cat-list-item'));
    for (let i = catListItems.length - 1; i >= 0; i--) {
      if (!catListItems[i]?.isStatic) {
        this.currentSelectedItem = catListItems[i]; /* 1 */
        break;
      }
    }
    this.currentSelectedItem?.setAttribute('aria-selected', 'true'); /* 2 */
    this.ariaActiveDescendantId = this.currentSelectedItem?.getAttribute('id')!; /* 3 */
  }

  /**
   * Select current selected item in dropdown
   */
  focusOnCurrentItem() {
    const element = this._catDropdownPanel.shadowRoot?.querySelector('.cat-c-dropdown-panel__body');
    if (element && this.currentSelectedItem) {
      scrollIntoView(this.currentSelectedItem as HTMLElement, element as HTMLElement);
    }
    this.currentSelectedItem?.focus();
  }

  /**
   * Remove aria selected
   * 1) Remove aria-selected from all list items and set currentSelectedItem to none
   */
  private removeAriaSelected() {
    const catListItems = Array.from(this.querySelectorAll('cat-list-item'));
    for (let i = catListItems.length - 1; i >= 0; i--) {
      if (catListItems[i]?.getAttribute('aria-selected') === 'true') {
        catListItems[i]?.removeAttribute('aria-selected');
        setTimeout(() => {
          this.currentSelectedItem = undefined;
        }, 1);
      }
      const catListLink = catListItems[i]?.shadowRoot?.querySelector('.cat-c-list-item__link');
      catListLink?.setAttribute('aria-selected', this.value === catListItems[i].value ? 'true' : 'false');
    }
  }

  /**
   * Update the scroll of the dropdown panel body
   * 1)If selected item is not defined, the dropdown panel scroll is set to top.
   */
  private updateScroll(scrollTop = 0) {
    const dropdownPanelBody = this?.shadowRoot?.querySelector('cat-dropdown-panel')?.shadowRoot?.querySelector('.cat-c-dropdown-panel__body');
    if (dropdownPanelBody) {
      dropdownPanelBody.scrollTop = scrollTop; /* 1 */
    }
  }

  /**
   * Clear the input field on click
   * 1) Set active, clear after click
   * 2) Clear the input value after click
   * 3) Set the focus on the input after the clear
   * 4) remove the aria selected and close the panel
   */
  private closeSearchForm() {
    this.isActive = false;
    this.value = '';
    this._catInputElement.value = '';
    this._catInputElement.focus();
    this.isFocusedIn = false;
    this.emitPanelCloseEvent();
    this.currentFocusedIndex = -1;
  }

  /*
  1) If the value is there and you click on escape then the panel will get closed and return focus to the trigger button.
  2) If the value is space or there is no value and you click on escape then the panel will get closed,remove the aria-selected, clear the input value and return focus to the trigger button.
  */
  private onPressingEscape() {
    /* 1 */
    if (this.value?.length && this.value !== ' ') {
      this.isActive = false;
      this.emitPanelCloseEvent();
      this.currentFocusedIndex = -1;
      this._catInputElement.focus();
    } else {
      /* 2 */
      this.closeSearchForm();
    }
  }

  /*
   * If the dropdown panel is active but not arrowed into yet and arrow down (or arrow up when dropdown is above search form)
   * is keyed, move the `aria-selected` state into the first list item.
   * A while condition is added, so that if there are disabled list items in panel, then it will be skipped and the focus will be kept to the first non-disabled list item.
   */
  private onPressingArrowEvents(eventKey: string) {
    let currentItem: BlocksListItem | undefined | null = this.querySelector('cat-list')?.querySelector('cat-list-item');

    while (currentItem?.isStatic) {
      currentItem = currentItem.nextElementSibling as BlocksListItem;
    }

    this.currentSelectedItem = currentItem;
    this.currentSelectedItem?.setAttribute('aria-selected', 'true');
    this.ariaActiveDescendantId = this.currentSelectedItem?.getAttribute('id')!;
    this.isFocusedIn = true;
    if (eventKey === KEYS.DOWN_ARROW) {
      setTimeout(() => {
        this.focusOnCurrentItem();
      }, 1);
    } else if (eventKey === KEYS.UP_ARROW) {
      setTimeout(() => {
        this.setLastItemAsCurrentItem();
        this.focusOnCurrentItem();
      }, 1);
      if (this.currentSelectedItem && this.previousElementSibling === null) {
        this.lastItemInDropdownPanel();
      }
    }
  }

  // Function to call keyboardLastItemFocus on the dropdown panel
  private lastItemInDropdownPanel() {
    const dropdownPanel = this._catDropdownPanel as BlocksDropdownPanel;
    dropdownPanel.keyboardLastItemFocus();
  }

  /*
   * When focused in panel and tabbed, focus on the clear button in the input
   * And clear the current focussed item inside dropdown panel
   */
  private onPressingTab() {
    setTimeout(() => {
      this.isActive = false;
      this.emitPanelCloseEvent();
      this.currentFocusedIndex = -1;
      const clearButton = this.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-search-form__clear-button');

      if (this.value && clearButton) {
        clearButton.shadowRoot?.querySelector<HTMLButtonElement>('.cat-c-button')?.focus();
      } else {
        this._catInputElement.focus();
      }
    }, 1);
    this.isFocusedIn = false;
    this.removeAriaSelected();
    this.updateScroll();
  }

  /*
   * 12)while condition added so that, it checks if the PreviousElementsibling is disabled or not.
   * If disabled, then it keeps looping until it finds the previous non-disabled list-item.
   * If it reaches the top , and if there is no such non-disabled items,
   * then setLastItemAsCurrentItem is called so that the focus returns back to the last non-disabled item.
   */
  private onPressingArrowUp() {
    let currentItem = this.currentSelectedItem?.previousElementSibling as BlocksListItem;

    while (currentItem?.isStatic) {
      currentItem = currentItem.previousElementSibling as BlocksListItem;
    }
    if (currentItem === null) {
      this.currentSelectedItem?.removeAttribute('aria-selected');
      this.setLastItemAsCurrentItem();
      this.focusOnCurrentItem();
      return;
    }

    this.currentSelectedItem?.removeAttribute('aria-selected');
    this.currentSelectedItem = <BlocksListItem>currentItem;
    this.currentSelectedItem.setAttribute('aria-selected', 'true');
    this.ariaActiveDescendantId = this.currentSelectedItem.getAttribute('id')!;
    setTimeout(() => {
      this.focusOnCurrentItem();
    }, 1);
  }

  /*
   * 13)while condition added so that, it checks if the nextElementsibling is disabled or not.
   * If disabled, then it keeps looping until it finds the next non-disabled list-item.
   * If it reaches the end , and if there is no such non-disabled items,
   * then setFirstListItemAsCurrentItem is called so that the focus returns back to the first non-disabled item.
   */
  private onPressingArrowDown() {
    let currentItem = this.currentSelectedItem?.nextElementSibling as BlocksListItem;

    while (currentItem?.isStatic) {
      currentItem = currentItem.nextElementSibling as BlocksListItem;
    }
    if (currentItem === null) {
      this.currentSelectedItem?.removeAttribute('aria-selected');
      this.setFirstListItemAsCurrentItem();
      this.focusOnCurrentItem();
      return;
    }

    this.currentSelectedItem?.removeAttribute('aria-selected');
    this.currentSelectedItem = <BlocksListItem>currentItem;
    this.currentSelectedItem.setAttribute('aria-selected', 'true');
    this.ariaActiveDescendantId = this.currentSelectedItem.getAttribute('id')!;
    setTimeout(() => {
      this.focusOnCurrentItem();
    }, 1);
  }

  /*
   * When focused in panel and enter is selected on item in dropdown, change the value to the list-item value
   */
  private onPressingEnter() {
    const element = this.currentSelectedItem?.value;
    this._catInputElement.value = element as string;
    this.value = element as string;
    this.removeAriaSelected();
    this.isFocusedIn = false;
  }
  /**
   * Handle on keydown
   * 1) If the dropdown panel is open and escape is keyed, close the menu and return focus to the trigger button
   * 2) If the dropdown panel is active but not arrowed into yet and arrow down (or arrow up when dropdown is above search form)
   *  is keyed, move the `aria-selected` state into the first list item.
   * A while condition is added, so that if there are disabled list items in panel, then it will be skipped and the focus will be kept to the first non-disabled list item.
   * 3) Otherwise, if the item is focused in, run various keydown events
   * 4) When focused in panel and tabbed, focus on the clear button in the input
   * 5) When focused in panel and enter is selected on item in dropdown, change the value to the list-item value
   * prop ,also that item should not be disabled and
   * 6) If current selected item has previous element and arrow up is selected, move `aria-selected to the previous item
   * 7) Otherwise, if If current selected item has next element and arrow down is selected, move `aria-selected to the next item
   * 8) Otherwise, if 6 isn't true when arrow up is selected when first item is aria-selected, then return the `aria-selected` state
   * to the last item
   * 9) Otherwise, if 7 isn't true and arrow down is selected when last item is aria-selected, then return the `aria-selected` state
   * to the first item
   * 10) If the dropdown panel is active but not arrowed into yet and tab is keyed, move focus to the clear button and remove
   * 11) Update the panel scroll when the item is focused on.
   * 12)while condition added so that, it checks if the PreviousElementsibling is disabled or not. If disabled, then it keeps looping
     until it finds the previous non-disabled list-item. If it reaches the top , and if there is no such non-disabled items, then setLastItemAsCurrentItem
     is called so that the focus returns back to the last non-disabled item.
   * 13)while condition added so that, it checks if the nextElementsibling is disabled or not. If disabled, then it keeps looping
     until it finds the next non-disabled list-item. If it reaches the end , and if there is no such non-disabled items, then focusOnFirstitem
     is called so that the focus returns back to the first non-disabled item.
   * 14) Use control + option + spacebar to trigger the dropdown without typing
   */

  private handleKeyDown(e: KeyboardEvent) {
    /* 1 */
    if (this.isActive === true && e.key === KEYS.ESCAPE) {
      this.onPressingEscape();
      return;
    }

    /* 14 */
    if (this.isActive === false) {
      this.keysPressed[e.key] = true;
      if (this.keysPressed['Control'] && this.keysPressed['Alt'] && e.key === KEYS.SPACE) {
        this.isActive = true;
        this.emitPanelOpenEvent();
      }
    }

    if (!this.isActive && e.key === KEYS.ENTER && ((this.value && this.value?.length > 0) || this.openOnClick)) {
      this.isActive = true;
      this.emitPanelOpenEvent();
    }

    /* 2 */
    if (
      (this.isActive === true && !this.isFocusedIn && e.key === KEYS.DOWN_ARROW) ||
      (this.isActive === true && !this.isFocusedIn && e.key === KEYS.UP_ARROW)
    ) {
      if (!this._checkboxList.length) {
        this.onPressingArrowEvents(e.key);
      } else if ((e.key === KEYS.DOWN_ARROW || e.key === KEYS.UP_ARROW) && this._checkboxHasHoverList.length) {
        this.handleCheckboxNavigation(e);
      }
    } else if (this.isActive === true && !this.isFocusedIn && e.key === KEYS.TAB) {
      /* 10 */
      setTimeout(() => {
        this.isActive = false;
        this.emitPanelCloseEvent();
        this._catInputElement.focus();
      }, 1);
    } else if (this.isFocusedIn === true) {
      /* 3 */
      if (e.key === KEYS.TAB) {
        /* 4 */
        this.onPressingTab();
      }
      if (e.key === KEYS.ENTER) {
        /* 5 */
        if (this.currentSelectedItem?.disabled) {
          return;
        }
        this.onPressingEnter();
      }
      if (this.currentSelectedItem && this.previousElementSibling === null && e.key === KEYS.UP_ARROW) {
        this.lastItemInDropdownPanel();
      }
      if (this.currentSelectedItem?.previousElementSibling && e.key === KEYS.UP_ARROW) {
        /* 12 */
        this.onPressingArrowUp();
      } else if (this.currentSelectedItem?.nextElementSibling && e.key === KEYS.DOWN_ARROW) {
        /* 13 */
        this.onPressingArrowDown();
      } else if (e.key === KEYS.UP_ARROW) {
        /* 8 */
        this.currentSelectedItem?.removeAttribute('aria-selected');
        this.setLastItemAsCurrentItem();
        this.focusOnCurrentItem();
      } else if (e.key === KEYS.DOWN_ARROW) {
        /* 9 */
        this.currentSelectedItem?.removeAttribute('aria-selected');
        this.setFirstListItemAsCurrentItem();
        this.focusOnCurrentItem();
      }
    }
    if (e.key === KEYS.ENTER) {
      if (!this._checkboxList.length || !this._checkboxList[this.currentFocusedIndex]?.disabled) {
        this.emitOnSearch();
      }
    }
  }

  /**
   * checkbox-field navigation
   */

  private handleCheckboxNavigation(e: KeyboardEvent): void {
    this.currentFocusedIndex = this.arrowNavigationController.checkboxArrowNavigation(
      e,
      this.currentFocusedIndex,
      this._checkboxHasHoverList.length - 1
    );
    this._checkboxHasHoverList[this.currentFocusedIndex].focus();
    if (e.key === KEYS.UP_ARROW && this.currentFocusedIndex === this._checkboxList.length - 1) {
      this.lastItemInDropdownPanel();
    }
  }

  /**
   * Handle key up
   * 1) Remove the multi-key-selection on key up after keydown combo is selected
   */
  private handleKeyUp(e: KeyboardEvent) {
    delete this.keysPressed[e.key]; /* 1 */
  }

  /**
   * Triggered when user clicks on search-form_input-field
   * 1. When user clicks on the input-field when search-form is closed and already value
   *  is present, open the search-form if it is not mobile version
   * 2.If it is mobile version, open the search-form on click.
   */
  private inputFocus() {
    if (!this.isActive) {
      if (!this.isMobile && ((this.value && this.value?.length > 0) || this.openOnClick)) {
        this.isActive = true; /* 1 */
        this.emitPanelOpenEvent();
      } else if (this.isMobile) {
        this.isActive = true;
        this.emitMobilePanelOpenEvent(); /* 2 */
      }
    }
  }
  /**
   * Open mobile function
   *  @fire emit OpenMobileOverlay Event when we click search input on mobile screens
   * 1. preserve app's actual overflow value to reset it later on close mobile flyout
   * 2. reset the position of search box to the top when clicked at mobile
   */
  private mobileClick() {
    this.setDropdownPanelDisplay('none');

    if (!this.isOverlay) {
      this.isOverlay = true;
      this._catBackButton.style.display = 'block';

      const body = this.queryBody;
      this.appBodyOverflow = body.style.overflow; // 1
      body.style.overflow = 'hidden';

      if (this.isPopoverEnabled) {
        this._searchFormMobileBody?.showPopover();
      }
      this._searchFormElement.style.display = 'none';
      this._searchFormMobileBody.style.display = 'block';

      if (this.value?.length || this.openOnClick) {
        this.setDropdownPanelDisplay('block');
      }
      setTimeout(() => {
        this._searchMobileInput.inputMode = 'text';
        this._searchMobileInput.focus();
      });
    }
  }

  /**
   * Close mobile function
   * @fire emit closeMobileOverlay Event when we click back button in search input on mobile screens
   * 1. Reset actual overflow value to bring app's scroll back to previous state before mobile flyout
   */
  private closeMobile() {
    if (this.isOverlay) {
      this.isOverlay = false;
      this.setDropdownPanelDisplay('none');
      this._catBackButton.style.display = 'none';

      const body = this.queryBody;
      body.style.overflow = this.appBodyOverflow; // 1

      if (this.isPopoverEnabled) {
        this._searchFormMobileBody?.hidePopover();
      }

      this._searchFormElement.style.display = 'block';
      this._searchFormMobileBody.style.display = 'none';
      this._searchMobileInput.blur();
    }
  }

  /**
   * Emit open event when mobile panel is opened
   */
  private emitMobilePanelOpenEvent() {
    this.dispatch({ eventName: 'bl-mobile-overlay-open', detailObj: { value: this.value } }) as BlocksMobileOverlayOpenEvent;
  }

  /**
   * This method has been to trigger whenever the list slot changes in the dropdown panel
   */
  private handleListSlotChange() {
    if (this.value) {
      this.assignSearchText(this.value);
    }
    this.catListUpdated();
  }

  /**
   * Emit open event when mobile panel is closed
   */
  private emitMobilePanelCloseEvent() {
    this.dispatch({ eventName: 'bl-mobile-overlay-close', detailObj: { value: this.value } }) as BlocksMobileOverlayCloseEvent;
  }

  /**
   * @fires bl-search
   */
  private emitOnSearch() {
    this.dispatch({
      eventName: 'bl-search',
      detailObj: {
        value: this.value
      }
    }) as BlocksSearchEvent;
  }

  /**
   * Called when back button is clicked on mobile panel
   */
  private onBackButtonClick() {
    this.isActive = false;
    this.emitMobilePanelCloseEvent();
  }

  /** Check if cat-list is in a slot and set the catList var */
  private getCatList(): BlocksList {
    let catList: BlocksList;
    if (this.querySelector<BlocksList>('cat-list')) {
      catList = this.querySelector<BlocksList>('cat-list')!;
    } else if (this.querySelector('slot')) {
      const nodes: Array<Node> | undefined = this.querySelector('slot')?.assignedNodes({ flatten: false });
      catList = nodes?.[0] as BlocksList;
    }
    return catList!;
  }

  /** To assign input value to searchText of list item */
  private assignSearchText(input: string) {
    const searchText = input?.trim();

    setTimeout(() => {
      const catList = this.getCatList();
      /* 2 */
      if (catList) {
        const catListItems = catList?.querySelectorAll<BlocksListItem>('cat-list-item');
        if (catListItems) {
          catListItems.forEach((element: BlocksListItem) => {
            element.searchText = searchText;
          });
        }
      }

      if (this._checkboxList.length !== 0) {
        this._checkboxList.forEach((element: BlocksCheckboxField) => {
          element.searchText = searchText;
        });
      }
    }, 1);
  }

  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassNames}">
        <div part="body-loader" class="cat-c-search-form-loader">
          <cat-skeleton part="body-skeleton" exportparts=${this.getExportParts('skeleton-body', 'skeleton')}> </cat-skeleton>
          ${!this.hideSearchIcon
            ? html`<cat-icon-search
                part="icon-search"
                exportparts=${this.getExportParts('search', 'icon')}
                class="cat-c-search-form__icon-loader"
              ></cat-icon-search>`
            : nothing}
        </div>

        ${this.hasFieldNote()
          ? html`<div part="field-note-loader" class="cat-c-search-form__fieldnote-loader">
              <cat-skeleton
                part="field-note-skeleton"
                exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                size="sm"
                width=${this.size === 'md' ? '80px' : '128px'}
              ></cat-skeleton>
            </div>`
          : html`${nothing}`}
      </div>
    `;
  }

  private hasFieldNote() {
    return this.fieldNote || this.errorNote || this.warningNote || this.hasFieldNoteSlot || this.hasErrorSlot || this.hasWarningSlot;
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
  }

  private renderBody(renderBackButton = false, overlayFieldId: string | null = null) {
    return html`
      <div part="body" class="cat-c-search-form__body" role="search">
        ${renderBackButton
          ? html` <cat-button
              part="back-button"
              exportparts=${this.getExportParts('button-back', 'button')}
              round
              @click=${this.onBackButtonClick}
              ?hideText=${true}
              class="cat-c-search-form__back-button"
            >
              ${this.backButtonText}
              <cat-icon-arrow-back part="icon-arrow-back" exportparts=${this.getExportParts('arrow-back', 'icon')} slot="after"></cat-icon-arrow-back>
            </cat-button>`
          : nothing}

        <div part="typeahead-wrapper" class="cat-c-search-form__typeahead-wrapper">
          <div part="typeahead" class="cat-c-search-form__typeahead">
            <input
              part="input"
              class="cat-c-search-form__input"
              id="${ifDefined(overlayFieldId ? overlayFieldId : this.fieldId)}"
              name="${ifDefined(this.name)}"
              .value="${this.value ?? ''}"
              placeholder="${ifDefined(this.placeholder)}"
              aria-autocomplete="list"
              aria-activedescendant="${ifDefined(this.ariaActiveDescendantId ?? undefined)}"
              aria-controls="${ifDefined(this.ariaControlsId ?? undefined)}"
              aria-expanded="${ifDefined(this.isActive)}"
              role="combobox"
              inputmode=${this.isMobile ? 'none' : 'text'}
              enterkeyhint="search"
              aria-describedby=${ifDefined(this.ariaDescribedBy)}
              aria-required="${ifDefined(this.required || undefined)}"
              ?disabled="${this.disabled}"
              @click=${this.inputFocus}
              @keydown=${this.handleKeyDown}
              @keyup=${this.handleKeyUp}
              @input=${(e: Event) => this.onChanged(e)}
              maxlength=${ifDefined(this.maxlength)}
              ?autofocus=${this.autoFocus}
              autocomplete="${this.autocomplete}"
            />
            <label
              part="part-label"
              class="cat-c-search-form__label cat-u-is-vishidden"
              for="${ifDefined(overlayFieldId ? overlayFieldId : this.fieldId)}"
              inert=${true}
              >${this.label}</label
            >
            <div part="icon-container" class="cat-c-search-form__icon-container">
              <div part="icon-after" class="cat-c-search-form__icon cat-c-search-form__icon-after">
                ${this.value?.length
                  ? html`
                      <cat-button
                        part="clear-button"
                        exportparts=${this.getExportParts('button-clear', 'button')}
                        @click=${this.clearSearchForm}
                        class="cat-c-search-form__clear-button"
                        ?hideText=${true}
                        variant="ghost"
                        size="sm"
                      >
                        ${this.clearButtonText}
                        <cat-icon-close part="icon-close" exportparts=${this.getExportParts('close', 'icon')} slot="after"></cat-icon-close>
                      </cat-button>
                    `
                  : nothing}
              </div>
              ${this.status === 'error'
                ? html` <cat-icon-warning-diamond-filled
                    part="status-icon"
                    exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
                    color="error"
                    class="cat-c-search-form__error-icon cat-c-search-form__icon"
                    .iconTitle=${this.errorIconTitle}
                  ></cat-icon-warning-diamond-filled>`
                : nothing}
              ${this.status === 'warning'
                ? html` <cat-icon-warning-triangle-filled
                    part="status-icon"
                    exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
                    color="knockout-brand"
                    .iconTitle=${this.warningIconTitle}
                    class="cat-c-search-form__warning-icon cat-c-search-form__icon"
                  ></cat-icon-warning-triangle-filled>`
                : nothing}
              ${(this.value?.length || this.status === 'error' || this.status === 'warning') &&
              !(this.buttonText || this.hasBeforeSlot || this.hasAfterSlot || this.hideSearchIcon) &&
              !this.disabled
                ? html`<span part="separator" class="cat-c-search-form-icon--separator"> </span>`
                : nothing}
              ${!(this.buttonText || this.hasAfterSlot || this.hasBeforeSlot || this.hideSearchIcon)
                ? html`<cat-icon-search
                    part="icon-search"
                    exportparts=${this.getExportParts('search', 'icon')}
                    class="cat-c-search-form__icon cat-c-search-form__icon-before"
                  ></cat-icon-search>`
                : nothing}
            </div>
            ${this.isEmpty || (!renderBackButton && this.isMobile)
              ? nothing
              : html`
                  <cat-dropdown-panel
                    part="dropdown-panel"
                    popover=${ifDefined(this.isPopoverEnabled && !this.isMobile ? 'manual' : undefined)}
                    exportparts=${this.getExportParts('dropdown-panel')}
                    class="cat-c-search-form__dropdown-panel"
                    id=${ifDefined(this.ariaControlsId ?? undefined)}
                    @keydown=${this.handleKeyDown}
                    ?hasScroll=${!this.isMobile && true}
                    .scrollOffset=${this.scrollOffset}
                  >
                    <slot part="part-default" @bl-list-slot-change=${this.handleListSlotChange} @bl-select=${this.toggleActive}></slot>
                  </cat-dropdown-panel>
                `}
          </div>
          <slot part="part-custom" name="custom"></slot>
        </div>
        <cat-button
          part="submit"
          exportparts=${this.getExportParts('button-submit', 'button')}
          class="cat-c-search-form__submit-button"
          ?hideText=${this.hideText}
          text=${ifDefined(this.buttonText)}
          size=${this.size}
          variant="primary"
          @click=${this.emitOnSearch}
          ?disabled="${this.disabled}"
        >
          ${this.buttonText}
          <span part="after-wrapper" slot="after" class="cat-c-search-form__submit-button-icon">
            <slot part="part-after" name="after"></slot>
          </span>
          <span part="before-wrapper" slot="before" class="cat-c-search-form__submit-button-icon">
            <slot part="part-before" name="before"></slot>
          </span>
        </cat-button>
      </div>
    `;
  }

  private renderFieldNote() {
    const itemTemplates: BlocksListItem[] = [];

    const dropdownListItems = Array.from(this.querySelectorAll<BlocksListItem>('cat-list-item'));

    dropdownListItems?.forEach((item) => {
      item.setAttribute('value', typeof item.value === 'object' ? <string>item.value.value : <string>item.value);
      item.setAttribute('listItemRole', (item.listItemRole = item.listItemRole !== 'listitem' ? item.listItemRole : 'option'));
      const newItem = unsafeHTML(item.outerHTML);
      itemTemplates.push(<BlocksListItem>newItem);
    });

    return html`
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
        class="cat-c-search-form__field-note-wrapper"
      >
        <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
      </cat-field-note>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-search-form__field-note cat-c-search-form__error-note-wrapper"
        .status=${'error'}
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.errorNote || this.hasErrorSlot ? this.ariaDescribedBy : undefined)}
      >
        <slot part="part-error" name="error">${this.errorNote}</slot>
      </cat-field-note>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-search-form__field-note cat-c-search-form__warning-note-wrapper"
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.warningNote || this.hasWarningSlot ? this.ariaDescribedBy : undefined)}
      >
        <slot part="part-warning" name="warning">${this.warningNote}</slot>
      </cat-field-note>

      <cat-list
        part="accessibility-list"
        exportparts=${this.getExportParts('list')}
        aria-hidden=${this.isActive === true ? false : true}
        class="cat-c-search-form__accessibility-list"
        listRole=${ifDefined(!this.isEmpty ? this.arialListRole : undefined)}
        arialabel=${ifDefined(this.arialListLabel)}
      >
        ${itemTemplates}
      </cat-list>
    `;
  }

  private mobileOverlaySearch(componentClassNames: DirectiveResult<typeof ClassMapDirective>) {
    const randomNumber = Math.floor(Math.random() * 1000);
    const overlayFieldId = this.fieldId ? this.fieldId + randomNumber : nanoid();
    return html`
      <div
        part="base"
        class="cat-c-search-form--mobile ${componentClassNames}"
        popover=${ifDefined(this.isPopoverEnabled && this.isMobile ? 'manual' : undefined)}
      >
        ${this.renderBody(true, overlayFieldId)} ${this.renderFieldNote()}
      </div>
    `;
  }

  private renderDefaultSearch(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div
        part="base"
        class="cat-c-search-form ${componentClassName}"
        popover=${ifDefined(this.isPopoverEnabled && this.isMobile ? 'manual' : undefined)}
      >
        ${this.renderBody()} ${this.renderFieldNote()}
      </div>
    `;
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('', {
      'cat-c-search-form--with-button': this.buttonText || this.hasAfterSlot || this.hasBeforeSlot,
      'cat-is-disabled': this.disabled,
      'cat-is-active': this.isActive === true,
      'cat-is-mobile': this.isMobile === true,
      'cat-is-overlay': this.isOverlay === true,
      'cat-is-focused-in': this.isFocusedIn === true,
      'cat-c-search-form--large': this.size === 'lg',
      'cat-c-search-form--medium': this.size === 'md',
      'cat-c-search-form--small': this.size === 'sm',
      'cat-is-error': this.status === 'error',
      'cat-is-warning': this.status === 'warning',
      'cat-is-dirty': this.value?.length,
      'cat-c-search-form--loader': this.isLoading === true,
      'cat-c-search-form--has-before': !this.hasAfterSlot && this.hasBeforeSlot,
      'cat-c-search-form--has-after': this.hasAfterSlot,
      'cat-c-search-form--has-field-note': this.fieldNote || this.hasFieldNoteSlot,
      'cat-c-search-form--has-error': this.errorNote || this.hasErrorSlot,
      'cat-c-search-form--has-warning': this.warningNote || this.hasWarningSlot
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassName);
    }

    this.setAriaDescribedBy();

    return html`
      ${this.renderDefaultSearch(componentClassName)} ${this.isMobile ? html` ${this.mobileOverlaySearch(componentClassName)}` : nothing}
    `;
  }
}

if (customElements.get('cat-search-form') === undefined) {
  customElements.define('cat-search-form', BlocksSearchForm);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-search-form': BlocksSearchForm;
  }
}
